import { faCopy } from '@fortawesome/free-regular-svg-icons';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import templateReplace from 'es6-template-strings';
import { object } from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';

import { get } from 'ravenjs/utils/lodash';
import { Box, Typography } from 'ravenjs/lib';
import Row from 'ravenjs/lib/Row';
import Button from 'ravenjs/lib/Button';
import Tooltip from 'ravenjs/lib/Tooltip';
import FaIcon from 'ravenjs/lib/FaIcon';

import { MODALS } from 'constants/modals';
import { CASE_DETAILS } from 'constants/tickets';
import { selectors as uiSelectors } from 'modules/ui';
import { sanitizeMarkup } from 'utils/sanitize';
import { DEFAULT_COLORS } from 'constants/colors';

import Modal from '../Modal';

const AnswerStyled = styled(Box)`
    padding: 0.313rem;
    max-height: 250px;
    overflow-y: auto;
    ::-webkit-scrollbar {
        width: 0.5rem;
    }
    ::-webkit-scrollbar-track {
        background: ${DEFAULT_COLORS.GREY_4};
    }
    ::-webkit-scrollbar-thumb {
        background: ${DEFAULT_COLORS.BLUE};
        border-radius: 0;
    }
`;

function CaseAnswerOverwriteModal({ modal }) {
    const [selectedItem, setSelectedItem] = useState('');
    const newAnswer = get(modal, 'data.response.errors.new_answer', '');
    const updatedBy = get(modal, 'data.response.errors.answer_updated_by', '');
    const updatedAt = get(modal, 'data.response.errors.answer_updated_at', '');
    const onClickOverwrite = get(modal, 'data.handleCaseOverwriteAnswer', () => {});
    const onCancel = get(modal, 'data.handleReturnToCaseAnswerEditor', () => {});
    const formattedTime = new Date(updatedAt).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
    });

    const handleCopyAnswer = answer => {
        copyTextToClipboard(answer).then(() => {
            setSelectedItem(answer);
            setTimeout(() => {
                setSelectedItem('');
            }, 2000);
        });
    };

    const copyTextToClipboard = async answer => {
        if ('clipboard' in navigator) {
            await navigator.clipboard.writeText(answer);
            return '';
        } else {
            return document.execCommand('answer', true, answer);
        }
    };

    const renderContent = () => {
        return (
            <Box>
                <Typography>{CASE_DETAILS.CASE_OVERWRITE_ANSWER.DESCRIPTION}</Typography>
                <Row justify="flex-end">
                    <Typography
                        fontFamily="Roboto"
                        fontSize="0.75rem"
                        gutterRight="25px"
                        fontStyle="italic"
                    >
                        {templateReplace(CASE_DETAILS.CASE_OVERWRITE_ANSWER.UPDATED_BY, {
                            username: updatedBy,
                            time: formattedTime,
                        })}
                    </Typography>
                </Row>
                <AnswerStyled>
                    <Row justify="flex-end" margin="0 0.313rem 0 0">
                        <Tooltip
                            content={
                                selectedItem !== newAnswer
                                    ? CASE_DETAILS.CASE_OVERWRITE_ANSWER.TOOLTIP_CONTENT.COPY
                                    : CASE_DETAILS.CASE_OVERWRITE_ANSWER.TOOLTIP_CONTENT.COPIED
                            }
                        >
                            {selectedItem !== newAnswer ? (
                                <FaIcon
                                    icon={faCopy}
                                    onClick={() => handleCopyAnswer(newAnswer)}
                                    padding="0 0 0 5px"
                                />
                            ) : (
                                <FaIcon
                                    icon={faCheck}
                                    color={DEFAULT_COLORS.GREEN}
                                    padding="0 0 0 5px"
                                />
                            )}
                        </Tooltip>
                    </Row>
                    <Typography
                        fontFamily="Roboto"
                        fontSize="0.875rem"
                        gutterLeft="0.5rem"
                        color={DEFAULT_COLORS.LINK}
                        dangerouslySetInnerHTML={sanitizeMarkup(newAnswer)}
                    />
                </AnswerStyled>
                <Row margin="0" justify="space-evenly">
                    <Button margin="0 0.625rem 0 0" onClick={onClickOverwrite} color="secondary">
                        {CASE_DETAILS.CASE_OVERWRITE_ANSWER.BUTTONS.SAVE}
                    </Button>
                    <Button margin="0 1rem 0 0" onClick={onCancel}>
                        {CASE_DETAILS.CASE_OVERWRITE_ANSWER.BUTTONS.CANCEL}
                    </Button>
                </Row>
            </Box>
        );
    };

    return (
        <Modal
            hideActions
            content={renderContent()}
            id={MODALS.CASE_ANSWER_OVERWRITE_MODAL}
            open={get(modal, 'open', false)}
        />
    );
}

CaseAnswerOverwriteModal.propTypes = {
    modal: object.isRequired,
};

const mapStateToProps = createStructuredSelector({
    modal: uiSelectors.getModalById(MODALS.CASE_ANSWER_OVERWRITE_MODAL),
});

export { CaseAnswerOverwriteModal as CaseAnswerOverwriteModalUnwrapped };
export default connect(mapStateToProps, null)(CaseAnswerOverwriteModal);
